import { Stack } from '@mui/material'

import { C1Button } from 'c1ui'
import { QueryFilter } from 'components/core/search-filter/query'
import { DisplayModeContext } from 'lib/context/app'
import { getNonMetaFields, MetaFields } from 'lib/search'
import { useRouter } from 'next/router'
import { SearchUsersRequest } from 'pbts/c1/api/user/v1/user'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { UserDepartmentFilter } from './UserDepartmentFilter'
import { UserJobTitleFilter } from './UserJobTitleFilter'
import { UserManagerFilter } from './UserManagerFilter'
import { UserRoleFilter } from './UserRoleFilter'
import { UserStatusFilter } from './UserStatusFilter'

const nonMetaFields = getNonMetaFields(SearchUsersRequest)

export type UserFilterKeys = keyof Omit<SearchUsersRequest, MetaFields>
type UserFilterUpdate = Partial<Pick<SearchUsersRequest, UserFilterKeys>>
export type UserFilterOnChange = {
    onChange: (updated: UserFilterUpdate, shouldPropage?: boolean) => void
}

type UserFilterProps = UserFilterOnChange & {
    initialState?: SearchUsersRequest
    displayFilters: UserFilterKeys[]
}

export const UserFilters = ({ initialState, onChange, displayFilters }: UserFilterProps): React.ReactElement => {
    const [filters, setFilters] = useState(SearchUsersRequest.create(initialState ?? {}))

    const router = useRouter()
    const displayContext = useContext(DisplayModeContext)
    const isInModal = displayContext === 'dialog' || displayContext === 'drawer'

    const _onChange: UserFilterProps['onChange'] = useCallback(
        (update, shouldPropage): void => {
            let newFilters
            setFilters((old) => {
                newFilters = SearchUsersRequest.clone(old)
                SearchUsersRequest.mergePartial(newFilters, update)
                return SearchUsersRequest.equals(newFilters, old) ? old : newFilters
            })
            // react won't call setFilters recursively it seems...
            if (!newFilters) {
                return
            }
            shouldPropage && onChange(newFilters)

            // Don't modify URL when in modal or drawer context
            if (!isInModal) {
                const params = Object.fromEntries(
                    nonMetaFields
                        .map((f) => f.localName)
                        .map((k) => newFilters[k] && [k, newFilters[k]])
                        .filter((x) => !!x),
                )
                router.replace({ pathname: router.pathname, query: params }, undefined, { shallow: true })
            }
        },
        [router, setFilters, onChange, isInModal],
    )

    // TODO(kans): make/update QueryFilter's interface
    const onQueryChange = useCallback(
        (query: string, isLoading?: boolean): void => {
            _onChange({ query }, !isLoading)
        },
        [_onChange],
    )

    const shouldShowFilter = useCallback(
        (filterKey: UserFilterKeys): boolean => {
            return displayFilters.includes(filterKey)
        },
        [displayFilters],
    )

    const clear = useCallback((): void => {
        // Only clear fields that are displayed
        const clearedUpdate: Partial<SearchUsersRequest> = {}

        if (shouldShowFilter('query')) {
            clearedUpdate.query = ''
        }
        if (shouldShowFilter('userStatuses')) {
            clearedUpdate.userStatuses = []
        }
        if (shouldShowFilter('roleIds')) {
            clearedUpdate.roleIds = []
        }
        if (shouldShowFilter('managerIds')) {
            clearedUpdate.managerIds = []
        }
        if (shouldShowFilter('departments')) {
            clearedUpdate.departments = []
        }
        if (shouldShowFilter('jobTitles')) {
            clearedUpdate.jobTitles = []
        }

        _onChange(clearedUpdate, true)
    }, [_onChange, shouldShowFilter])

    const canClear = useMemo(
        (): boolean => {
            // Only show clear button if displayed filters have values
            const hasQuery = shouldShowFilter('query') && !!filters.query
            const hasUserStatuses = shouldShowFilter('userStatuses') && filters.userStatuses.length > 0
            const hasRoleIds = shouldShowFilter('roleIds') && filters.roleIds.length > 0
            const hasManagerIds = shouldShowFilter('managerIds') && filters.managerIds.length > 0
            const hasDepartments = shouldShowFilter('departments') && filters.departments && filters.departments.length > 0
            const hasJobTitles = shouldShowFilter('jobTitles') && filters.jobTitles && filters.jobTitles.length > 0

            return hasQuery || hasUserStatuses || hasRoleIds || hasManagerIds || hasDepartments || hasJobTitles
        },
        [filters, shouldShowFilter],
    )

    return <Stack direction="row" spacing={2} alignItems="flex-end">
        {shouldShowFilter('query') && (
            <QueryFilter
                autoFocus
                query={filters.query}
                setQuery={(q): void => onQueryChange(q as unknown as string, true)}
                onChange={onQueryChange}
            />
        )}
        {shouldShowFilter('userStatuses') && (
            <UserStatusFilter value={filters.userStatuses} onChange={_onChange} />
        )}
        {shouldShowFilter('roleIds') && (
            <UserRoleFilter value={filters.roleIds} onChange={_onChange} />
        )}
        {shouldShowFilter('managerIds') && (
            <UserManagerFilter value={filters.managerIds} onChange={_onChange} />
        )}
        {shouldShowFilter('departments') && (
            <UserDepartmentFilter value={filters.departments} onChange={_onChange} />
        )}
        {shouldShowFilter('jobTitles') && (
            <UserJobTitleFilter value={filters.jobTitles} onChange={_onChange} />
        )}
        {canClear && (
            <C1Button onClick={clear} variant="text" color="info">
                <FormattedMessage id="components_user_search_index_clear" defaultMessage="Clear" />
            </C1Button>
        )}
    </Stack>
}
